import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddToDriveIcon from '@mui/icons-material/AddToDrive';
import { toast } from 'react-toastify';
import DriveImportService from '../../../services/DriveImportService';
import palette from '../../../common/styles/palette';

interface DriveSingleImportModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  student: {
    user_id: number;
    user_name?: string;
    name?: string;
    has_debriefing_video_attachment?: boolean;
    urlVideoAttachment?: string | null;
  } | null;
  eventId: number | string;
  stationId: number | string;
}

export default function DriveSingleImportModal({
  open,
  onClose,
  onSuccess,
  student,
  eventId,
  stationId,
}: DriveSingleImportModalProps) {
  const [fileUrl, setFileUrl] = useState('');
  const [overwrite, setOverwrite] = useState(true);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const studentName = student?.user_name || student?.name || 'Aluno';
  const hasExistingVideo = Boolean(student?.has_debriefing_video_attachment);

  const handleClose = () => {
    if (loading) return;
    setFileUrl('');
    setErrorMsg(null);
    onClose();
  };

  const handleConfirm = async () => {
    const trimmed = fileUrl.trim();
    if (!trimmed) {
      setErrorMsg('Por favor, informe a URL ou o ID do arquivo do Google Drive.');
      return;
    }

    if (!student?.user_id) {
      setErrorMsg('Aluno não selecionado ou identificador inválido.');
      return;
    }

    setLoading(true);
    setErrorMsg(null);

    try {
      const response = await DriveImportService.single({
        file_url: trimmed,
        event_id: Number(eventId),
        station_id: Number(stationId),
        user_id: Number(student.user_id),
        overwrite,
      });

      if (response.data?.success) {
        toast.success(response.data.message || 'Vídeo enfileirado para sincronização com sucesso!');
        handleClose();
        onSuccess();
      } else {
        setErrorMsg(response.data?.message || 'Falha ao enfileirar importação.');
      }
    } catch (err: any) {
      const apiError =
        err?.response?.data?.message ||
        err?.response?.data?.Error ||
        err?.response?.data?.error ||
        err?.message ||
        'Erro ao tentar importar o vídeo do Google Drive.';
      setErrorMsg(apiError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${palette.border}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <AddToDriveIcon sx={{ color: palette.brand }} />
          <Typography variant="h6" component="span" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>
            Importar Vídeo do Google Drive
          </Typography>
        </Box>
        <IconButton
          aria-label="fechar"
          onClick={handleClose}
          disabled={loading}
          size="small"
          sx={{ color: palette.textMuted }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ color: palette.textMuted }}>
            Aluno selecionado:
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: palette.text }}>
            {studentName}
          </Typography>
        </Box>

        {hasExistingVideo && (
          <Alert severity="warning" sx={{ mb: 2.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Este aluno já possui um vídeo gravado/anexado para esta estação.
            </Typography>
            <Box sx={{ mt: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={overwrite}
                    onChange={(e) => setOverwrite(e.target.checked)}
                    color="warning"
                    disabled={loading}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Substituir vídeo existente
                  </Typography>
                }
              />
            </Box>
          </Alert>
        )}

        {errorMsg && (
          <Alert severity="error" sx={{ mb: 2.5 }} onClose={() => setErrorMsg(null)}>
            {errorMsg}
          </Alert>
        )}

        <TextField
          autoFocus
          label="URL ou ID do Arquivo no Google Drive"
          placeholder="https://drive.google.com/file/d/1XYZ987abc.../view"
          value={fileUrl}
          onChange={(e) => {
            setFileUrl(e.target.value);
            if (errorMsg) setErrorMsg(null);
          }}
          disabled={loading}
          fullWidth
          variant="outlined"
          helperText="O arquivo no Google Drive deve estar com compartilhamento aberto ('Qualquer pessoa com o link')."
          sx={{ mt: 1 }}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${palette.border}` }}>
        <Button onClick={handleClose} disabled={loading} color="inherit">
          Cancelar
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={loading || !fileUrl.trim()}
          variant="contained"
          sx={{
            bgcolor: palette.brand,
            '&:hover': { bgcolor: palette.brandDark },
            color: '#fff',
            minWidth: 140,
          }}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <AddToDriveIcon />}
        >
          {loading ? 'Importando...' : 'Importar Vídeo'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
